<template>
  <div class="email-config">
    <a-card title="邮箱配置" :bordered="false" class="mb-4">
      <a-form ref="formRef" :model="form" :rules="rules" layout="vertical">
        <a-row :gutter="24">
          <a-col :span="12">
            <a-form-item label="配置名称" field="configName">
              <a-input v-model="form.configName" placeholder="请输入配置名称"/>
            </a-form-item>
          </a-col>

          <a-col :span="12">
            <a-form-item label="是否默认配置" field="isDefault">
              <a-switch v-model="form.isDefault"/>
            </a-form-item>
          </a-col>

          <a-col :span="12">
            <a-form-item label="SMTP服务器" field="smtpHost">
              <a-input v-model="form.smtpHost" placeholder="请输入SMTP服务器地址"/>
            </a-form-item>
          </a-col>

          <a-col :span="12">
            <a-form-item label="SMTP端口" field="smtpPort">
              <a-input-number v-model="form.smtpPort" placeholder="请输入SMTP端口" style="width: 100%"/>
            </a-form-item>
          </a-col>

          <a-col :span="12">
            <a-form-item label="邮箱账号" field="username">
              <a-input v-model="form.username" placeholder="请输入邮箱账号"/>
            </a-form-item>
          </a-col>

          <a-col :span="12">
            <a-form-item label="邮箱密码" field="password">
              <a-input-password v-model="form.password" placeholder="请输入邮箱密码"/>
            </a-form-item>
          </a-col>

          <a-col :span="12">
            <a-form-item label="发送者邮箱" field="fromEmail">
              <a-input v-model="form.fromEmail" placeholder="请输入发送者邮箱地址"/>
            </a-form-item>
          </a-col>

          <a-col :span="12">
            <a-form-item label="发送者名称" field="fromName">
              <a-input v-model="form.fromName" placeholder="请输入发送者名称"/>
            </a-form-item>
          </a-col>

          <a-col :span="12">
            <a-form-item label="是否使用SSL" field="useSsl">
              <a-switch v-model="form.useSsl"/>
            </a-form-item>
          </a-col>

          <a-col :span="12">
            <a-form-item label="是否使用TLS" field="useTls">
              <a-switch v-model="form.useTls"/>
            </a-form-item>
          </a-col>

          <a-col :span="12">
            <a-form-item label="连接超时时间(毫秒)" field="timeout">
              <a-input-number v-model="form.timeout" placeholder="请输入连接超时时间" style="width: 100%"/>
            </a-form-item>
          </a-col>

          <a-col :span="12">
            <a-form-item label="字符编码" field="charset">
              <a-input v-model="form.charset" placeholder="请输入字符编码"/>
            </a-form-item>
          </a-col>

          <a-col :span="24">
            <a-form-item label="备注说明" field="remark">
              <a-textarea v-model="form.remark" placeholder="请输入备注说明" :auto-size="{ minRows: 3 }"/>
            </a-form-item>
          </a-col>
        </a-row>

        <a-form-item>
          <a-button type="primary" @click="handleSubmit" :loading="submitLoading">测试并提交</a-button>
          <a-button style="margin-left: 16px" @click="handleReset">重置</a-button>
        </a-form-item>
      </a-form>
    </a-card>
  </div>
</template>

<script setup>
import {ref, reactive, onMounted} from 'vue'
import {Message} from '@arco-design/web-vue'
import {getEmailConfig, saveEmailConfig} from '@/apis/system/email'

const formRef = ref()
const submitLoading = ref(false)
const form = reactive({
  id: 1, // 固定使用ID为1的配置
  configName: '',
  smtpHost: '',
  smtpPort: 465,
  username: '',
  password: '',
  fromEmail: '',
  fromName: '',
  useSsl: true,
  useTls: false,
  timeout: 5000,
  charset: 'UTF-8',
  isDefault: true,
  remark: ''
})

const rules = {
  configName: [{required: true, message: '请输入配置名称'}],
  smtpHost: [{required: true, message: '请输入SMTP服务器地址'}],
  smtpPort: [{required: true, message: '请输入SMTP端口'}],
  username: [{required: true, message: '请输入邮箱账号'}],
  password: [{required: true, message: '请输入邮箱密码'}],
  fromEmail: [{required: true, message: '请输入发送者邮箱地址'}]
}

// 获取邮箱配置
const fetchConfig = async () => {
  try {
    const res = await getEmailConfig()
    // 直接使用返回的数据（应该就是ID为1的配置）
    if (res.data) {
      Object.assign(form, res.data)
    }
  } catch (err) {
    console.error('获取邮箱配置失败:', err)
    Message.error('获取配置失败')
  }
}

const handleSubmit = async () => {
  const res = await formRef.value?.validate()
  if (!res) {
    submitLoading.value = true
    try {
      // 更新ID为1的配置
      let rs = await saveEmailConfig(form)
      if (rs.code === 0) {
        Message.success('保存成功')
        await fetchConfig() // 重新获取配置
      } else {
        Message.error(rs.message || '保存失败')
      }
    } catch (err) {
      console.error('保存邮箱配置失败:', err)
      Message.error('保存失败')
    } finally {
      submitLoading.value = false
    }
  }
}

const handleReset = () => {
  Object.assign(form, {
    id: 1,
    configName: '',
    smtpHost: '',
    smtpPort: 465,
    username: '',
    password: '',
    fromEmail: '',
    fromName: '',
    useSsl: true,
    useTls: false,
    timeout: 5000,
    charset: 'UTF-8',
    isDefault: true,
    remark: ''
  })
}

onMounted(() => {
  fetchConfig()
})
</script>

<style scoped>
.email-config {
  padding: 16px;
  background-color: #f5f5f5;
  min-height: calc(100vh - 60px);
}

.mb-4 {
  margin-bottom: 16px;
}
</style>